import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronRightIcon,
  ChevronDownIcon,
  PencilIcon,
  FolderIcon,
  FolderOpenIcon,
} from "@heroicons/react/24/outline";
import { documentSrc } from "../../dashboard/documents/utils";

export const CategoryTreeView = ({ categories, onEdit, expandedNodes = [], onToggleExpand }) => {
  const { t } = useTranslation();
  const [localExpanded, setLocalExpanded] = useState(expandedNodes || []);

  const toggleExpand = (categoryId) => {
    const newExpanded = localExpanded.includes(categoryId)
      ? localExpanded.filter((id) => id !== categoryId)
      : [...localExpanded, categoryId];
    setLocalExpanded(newExpanded);
    if (onToggleExpand) {
      onToggleExpand(newExpanded);
    }
  };

  const buildTree = (categories) => {
    const categoryMap = {};
    const rootCategories = [];

    // First pass: create a map of all categories
    categories.forEach((category) => {
      categoryMap[category.id] = {
        ...category,
        children: [],
      };
    });

    // Second pass: build the tree structure
    categories.forEach((category) => {
      const parentId = category.parent?.id || category.parent_category;
      if (parentId && categoryMap[parentId]) {
        categoryMap[parentId].children.push(categoryMap[category.id]);
      } else {
        rootCategories.push(categoryMap[category.id]);
      }
    });

    return rootCategories;
  };

  const renderCategory = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = localExpanded.includes(category.id);
    const indentLevel = level * 24;

    return (
      <div key={category.id} className="select-none">
        <div
          className={`flex items-center py-2 px-3 hover:bg-gray-50 rounded-md transition-colors ${
            level > 0 ? "ml-4" : ""
          }`}
          style={{ paddingLeft: `${indentLevel + 12}px` }}
        >
          {/* Expand/Collapse Button */}
          {hasChildren ? (
            <button
              onClick={() => toggleExpand(category.id)}
              className="mr-2 p-1 hover:bg-gray-200 rounded transition-colors"
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4 text-gray-600" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-gray-600" />
              )}
            </button>
          ) : (
            <div className="w-6 mr-2" />
          )}

          {/* Category Image or Folder Icon */}
          <div className="mr-2 flex-shrink-0">
            {category?.image ? (
              <img
                className="h-10 w-10 rounded-full object-cover"
                src={documentSrc(category.image)}
                alt={category?.image?.id ? category.image.title : "Image placeholder"}
              />
            ) : hasChildren && isExpanded ? (
              <FolderOpenIcon className="h-5 w-5 text-blue-500" />
            ) : (
              <FolderIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>

          {/* Category Title */}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">
              {category.displayTitle || category.title}
            </div>
            {category.description && (
              <div className="text-xs text-gray-500 truncate mt-0.5">
                {category.description.replace(/<[^>]*>/g, "").substring(0, 50)}
                {category.description.length > 50 ? "..." : ""}
              </div>
            )}
          </div>

          {/* Edit Button */}
          <button
            onClick={() => onEdit && onEdit(category)}
            className="ml-2 p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            title={t("Edit Category")}
          >
            <PencilIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="ml-2">
            {category.children.map((child) => renderCategory(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const tree = buildTree(categories);

  if (categories.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-2 text-sm">{t("No categories found")}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="divide-y divide-gray-200">
        {tree.map((category) => renderCategory(category, 0))}
      </div>
    </div>
  );
};

